interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

class CacheService {
  private static instance: CacheService;
  private readonly CACHE_PREFIX = "agco_lisaprint_cache_";
  private readonly DEFAULT_EXPIRY_HOURS = 5;

  private constructor() {}

  static getInstance(): CacheService {
    if (!CacheService.instance) {
      CacheService.instance = new CacheService();
    }
    return CacheService.instance;
  }

  /**
   * Stocke des données dans le cache avec expiration
   */
  set<T>(
    key: string,
    data: T,
    expiryHours: number = this.DEFAULT_EXPIRY_HOURS
  ): void {
    try {
      const now = Date.now();
      const expiresAt = now + expiryHours * 60 * 60 * 1000; // Convertir les heures en millisecondes

      const cacheItem: CacheItem<T> = {
        data,
        timestamp: now,
        expiresAt,
      };

      localStorage.setItem(this.CACHE_PREFIX + key, JSON.stringify(cacheItem));
    } catch (error) {
      console.error("Erreur lors du stockage en cache:", error);
    }
  }

  /**
   * Récupère des données du cache si elles ne sont pas expirées
   */
  get<T>(key: string): T | null {
    try {
      const cached = localStorage.getItem(this.CACHE_PREFIX + key);
      if (!cached) {
        return null;
      }

      const cacheItem: CacheItem<T> = JSON.parse(cached);
      const now = Date.now();

      if (now > cacheItem.expiresAt) {
        // Cache expiré, le supprimer
        this.remove(key);

        return null;
      }

      return cacheItem.data;
    } catch (error) {
      console.error("Erreur lors de la récupération du cache:", error);
      this.remove(key);
      return null;
    }
  }

  /**
   * Supprime un élément du cache
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(this.CACHE_PREFIX + key);
    } catch (error) {
      console.error("Erreur lors de la suppression du cache:", error);
    }
  }

  /**
   * Vérifie si une clé existe et n'est pas expirée
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Nettoie tous les éléments expirés du cache
   */
  cleanup(): void {
    try {
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter((key) => key.startsWith(this.CACHE_PREFIX));

      let cleanedCount = 0;
      cacheKeys.forEach((key) => {
        const originalKey = key.replace(this.CACHE_PREFIX, "");
        if (!this.has(originalKey)) {
          cleanedCount++;
        }
      });
    } catch (error) {
      console.error("Erreur lors du nettoyage du cache:", error);
    }
  }

  /**
   * Vide tout le cache
   */
  clear(): void {
    try {
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter((key) => key.startsWith(this.CACHE_PREFIX));

      cacheKeys.forEach((key) => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.error("Erreur lors du vidage du cache:", error);
    }
  }

  /**
   * Obtient les informations sur le cache
   */
  getInfo(): { totalItems: number; totalSize: number; keys: string[] } {
    try {
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter((key) => key.startsWith(this.CACHE_PREFIX));

      let totalSize = 0;
      cacheKeys.forEach((key) => {
        const item = localStorage.getItem(key);
        if (item) {
          totalSize += new Blob([item]).size;
        }
      });

      return {
        totalItems: cacheKeys.length,
        totalSize,
        keys: cacheKeys.map((key) => key.replace(this.CACHE_PREFIX, "")),
      };
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des infos du cache:",
        error
      );
      return { totalItems: 0, totalSize: 0, keys: [] };
    }
  }

  /**
   * Affiche les statistiques détaillées du cache dans la console
   */
  logStats(): void {
    const info = this.getInfo();
    const sizeInKB = Math.round(info.totalSize / 1024);
    const sizeInMB = Math.round((info.totalSize / 1024 / 1024) * 100) / 100;

    console.group("📊 Statistiques du Cache");

    console.groupEnd();
  }

  /**
   * Affiche les détails d'un élément spécifique du cache
   */
  logItemDetails(key: string): void {
    try {
      const cached = localStorage.getItem(this.CACHE_PREFIX + key);
      if (!cached) {
        return;
      }

      const cacheItem = JSON.parse(cached);
      const now = Date.now();
      const expiresIn = Math.round((cacheItem.expiresAt - now) / 1000 / 60);
      const size = new Blob([cached]).size;

      console.group(`📋 Détails de l'élément: ${key}`);

      console.groupEnd();
    } catch (error) {
      console.error(
        `❌ Erreur lors de l'affichage des détails de ${key}:`,
        error
      );
    }
  }
}

export default CacheService.getInstance();
